/* eslint-disable react-refresh/only-export-components */
import Cookies from 'js-cookie'
import {
  StoreEnhancer,
  combineReducers,
  configureStore,
  isRejectedWithValue,
} from '@reduxjs/toolkit'
import Stack from '@mui/material/Stack'
import { setupListeners } from '@reduxjs/toolkit/query'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import type { TypedUseSelectorHook } from 'react-redux'
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'

import { authApi } from './services/auth'
import { certificatesApi } from './services/certificates'
import { userApi } from './services/user'
import { eventsApi } from './services/events'
import { optionsApi } from './services/options'
import { sponsorshipApi } from './services/sponsorship'
import { registrationApi } from './services/registration'
import storeReducer, { initialState } from './store.slice'
import i18n from './i18n'

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
}) as StoreEnhancer

/**
 * Log a warning and show a toast!
 */
const rtkQueryErrorLogger: Middleware = (_api: MiddlewareAPI) => next => rawAction => {
  const action = rawAction as {
    payload: {
      data: {
        type: 'client_error' | 'server_error'
        errors: { code: string; detail: string; attr: string | null }[]
      }
    }
  }
  if (isRejectedWithValue(action)) {
    console.error({ action })
    if ('data' in action.payload) {
      const errorResponse = action.payload.data
      errorResponse.errors?.forEach(error => {
        if (error.code === 'authentication_failed') {
          const from =
            new URL(window.location.toString()).searchParams.get('from') ?? window.location.pathname
          const params = new URLSearchParams()
          params.set('from', from)
          window.location.href = `/logout?${params.toString()}`

          toast.error(i18n.t('Vous devez vous reconnecter pour effectuer cette action'), {
            toastId: error.code,
          })
        } else {
          const path = (error.attr ?? '__all__') === '__all__' ? i18n.t('Erreur') : error.attr
          toast.warn(
            <Stack direction="column">
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {path}: {error.detail}
              </div>
              {error.code === 'missing_user_profile' && (
                <a
                  href="https://www.sfctcv.org/ma-situation-professionnelle2/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {i18n.t('Compléter mon profil')}
                </a>
              )}
            </Stack>,
            {
              autoClose: 60000,
            }
          )
        }
      })
    }
  }

  return next(action)
}

const rootReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [certificatesApi.reducerPath]: certificatesApi.reducer,
  [eventsApi.reducerPath]: eventsApi.reducer,
  [registrationApi.reducerPath]: registrationApi.reducer,
  [optionsApi.reducerPath]: optionsApi.reducer,
  [sponsorshipApi.reducerPath]: sponsorshipApi.reducer,
  store: storeReducer,
})

export const setupStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      // adding the api middleware enables caching, invalidation, polling and other features of `rtk-query`
      getDefaultMiddleware()
        .concat(authApi.middleware)
        .concat(userApi.middleware)
        .concat(certificatesApi.middleware)
        .concat(eventsApi.middleware)
        .concat(registrationApi.middleware)
        .concat(optionsApi.middleware)
        .concat(sponsorshipApi.middleware)
        .concat(rtkQueryErrorLogger),
    enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(sentryReduxEnhancer),
    preloadedState,
  })

  setupListeners(store.dispatch)

  return store
}

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = AppStore['dispatch']
export type { StoreState } from './store.slice'

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

const token = Cookies.get('token') ?? null

export const store = setupStore({
  store: {
    ...initialState,
    token,
  },
})
